import { useState } from 'react';
import { Card, Tag, Button, Popconfirm, Spin, Empty, Typography, Space, List } from 'antd';
import {
  ExperimentOutlined,
  ScanOutlined,
  CheckCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import type { SurgicalProcedureRequirement, RequirementStatus, RequirementType } from '../surgeryService';
import {
  REQUIREMENT_STATUS_LABELS,
  REQUIREMENT_TYPE,
} from '../surgeryService';

const { Title } = Typography;

interface PreOpRequirementsCardProps {
  procedureId: number;
  requirements: SurgicalProcedureRequirement[];
  loading: boolean;
  onStatusChange: (requirementId: number, newStatus: RequirementStatus) => Promise<void>;
  onRegenerate: () => Promise<void>;
}

function timeAgo(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'recién';
  if (diffMin < 60) return `hace ${diffMin} min`;
  if (diffHrs < 24) return `hace ${diffHrs} ${diffHrs === 1 ? 'hora' : 'horas'}`;
  if (diffDays < 30) return `hace ${diffDays} ${diffDays === 1 ? 'día' : 'días'}`;
  return new Date(dateStr).toLocaleDateString('es-DO');
}

const STATUS_COLOR: Record<RequirementStatus, string> = {
  pending: 'orange',
  completed: 'green',
  waived: 'default',
};

const TYPE_GROUP_LABELS: Record<'lab' | 'imaging', { label: string }> = {
  lab: { label: 'Laboratorio' },
  imaging: { label: 'Imágenes' },
};

function groupRequirements(requirements: SurgicalProcedureRequirement[]) {
  const lab: SurgicalProcedureRequirement[] = [];
  const imaging: SurgicalProcedureRequirement[] = [];

  for (const req of requirements) {
    if (req.requirement_type === REQUIREMENT_TYPE.LAB_TEST || req.requirement_type === REQUIREMENT_TYPE.LAB_PANEL) {
      lab.push(req);
    } else if (req.requirement_type === REQUIREMENT_TYPE.IMAGING) {
      imaging.push(req);
    }
  }

  return { lab, imaging };
}

export function PreOpRequirementsCard({
  procedureId,
  requirements,
  loading,
  onStatusChange,
  onRegenerate,
}: PreOpRequirementsCardProps) {
  const [regenerating, setRegenerating] = useState(false);
  const [actionLoading, setActionLoading] = useState<Set<number>>(new Set());

  const handleRegenerate = async () => {
    setRegenerating(true);
    try {
      await onRegenerate();
    } finally {
      setRegenerating(false);
    }
  };

  const handleStatusChange = async (requirementId: number, status: RequirementStatus) => {
    setActionLoading((prev) => new Set(prev).add(requirementId));
    try {
      await onStatusChange(requirementId, status);
    } finally {
      setActionLoading((prev) => {
        const next = new Set(prev);
        next.delete(requirementId);
        return next;
      });
    }
  };

  const groups = groupRequirements(requirements);
  const hasAnyItems = groups.lab.length > 0 || groups.imaging.length > 0;

  const renderSection = (
    group: SurgicalProcedureRequirement[],
    type: 'lab' | 'imaging',
    icon: React.ReactNode
  ) => {
    if (group.length === 0) return null;

    return (
      <div style={{ marginBottom: 8 }}>
        <Title level={5} style={{ marginBottom: 8 }}>
          {icon} {TYPE_GROUP_LABELS[type].label}
        </Title>
        <List
          size="small"
          dataSource={group}
          renderItem={(req) => {
            const isPending = req.status === 'pending';
            const isUpdating = actionLoading.has(req.id);

            return (
              <List.Item
                actions={
                  isPending
                    ? [
                        <Popconfirm
                          key="complete"
                          title="¿Marcar este requisito como completado?"
                          onConfirm={() => handleStatusChange(req.id, 'completed')}
                          okText="Sí"
                          cancelText="Cancelar"
                        >
                          <Button
                            size="small"
                            type="primary"
                            icon={<CheckCircleOutlined />}
                            loading={isUpdating}
                          >
                            Completar
                          </Button>
                        </Popconfirm>,
                        <Popconfirm
                          key="waive"
                          title="¿Omitir este requisito?"
                          onConfirm={() => handleStatusChange(req.id, 'waived')}
                          okText="Sí"
                          cancelText="Cancelar"
                        >
                          <Button size="small" loading={isUpdating}>
                            Omitir
                          </Button>
                        </Popconfirm>,
                      ]
                    : undefined
                }
              >
                <List.Item.Meta
                  title={
                    <Space>
                      <Tag color={STATUS_COLOR[req.status]}>
                        {REQUIREMENT_STATUS_LABELS[req.status]}
                      </Tag>
                      <span>{req.requirement_label}</span>
                    </Space>
                  }
                  description={
                    req.status === 'completed' && req.completed_by
                      ? `Completado por ${req.completed_by.name} — ${req.completed_at ? timeAgo(req.completed_at) : ''}`
                      : undefined
                  }
                />
              </List.Item>
            );
          }}
        />
      </div>
    );
  };

  return (
    <Card
      title="Requisitos Pre-Operatorios"
      style={{ marginBottom: 16 }}
      extra={
        <Button
          icon={<ReloadOutlined />}
          onClick={handleRegenerate}
          loading={regenerating}
        >
          Regenerar
        </Button>
      }
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin size="small" />
        </div>
      ) : !hasAnyItems ? (
        <Empty
          description="Sin requisitos pre-operatorios configurados. Usá el botón Regenerar para generar sugerencias."
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      ) : (
        <>
          {renderSection(groups.lab, 'lab', <ExperimentOutlined />)}
          {renderSection(groups.imaging, 'imaging', <ScanOutlined />)}
        </>
      )}
    </Card>
  );
}
